import React, { useEffect, useState, useRef } from 'react';
import { toast } from 'sonner';
import { NxPanel, NxButton, NxChip } from '../components/nx';
import {
  FileSignature, Plus, X, Trash2, FileText, Loader2,
  UploadCloud, Eye, RefreshCw, FileCheck2, AlertCircle, Edit3,
} from 'lucide-react';
import { BACKEND_URL } from '../config/canonical';
import TemplateFieldEditor from '../components/TemplateFieldEditor';

const TOKEN_KEY = 'nexus_token';

const api = async (path, opts = {}) => {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${BACKEND_URL}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
    ...opts,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
  return data;
};

const CATEGORY_TONE = {
  policy: 'amber',
  credential: 'blue',
  contract: 'cyan',
  consent: 'purple',
  hr: 'amber',
  general: 'blue',
};
const CATEGORIES = ['general', 'policy', 'credential', 'contract', 'consent', 'hr', 'other'];
const fmtSize = (b) => !b ? '—' : b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`;

const OnboardingDocuments = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploader, setShowUploader] = useState(false);
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [editingFieldsFor, setEditingFieldsFor] = useState(null);

  // Upload form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('general');
  const [required, setRequired] = useState(true);
  const [file, setFile] = useState(null);
  const [uploadPct, setUploadPct] = useState(0);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api('/api/admin/document-templates');
      setTemplates(r.templates || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.toLowerCase().includes('pdf')) {
      toast.error('Only PDF files are accepted');
      return;
    }
    if (f.size > 25 * 1024 * 1024) {
      toast.error('PDF must be 25MB or smaller');
      return;
    }
    setFile(f);
    if (!name) setName(f.name.replace(/\.pdf$/i, ''));
  };

  const upload = async () => {
    if (!file) { toast.error('Choose a PDF file'); return; }
    if (!name.trim()) { toast.error('Enter a template name'); return; }
    setBusy(true);
    setUploadPct(0);
    try {
      const fd = new FormData();
      fd.append('name', name.trim());
      if (description.trim()) fd.append('description', description.trim());
      fd.append('category', category);
      fd.append('required', required ? 'true' : 'false');
      fd.append('file', file);
      const token = localStorage.getItem(TOKEN_KEY);
      // XHR for accurate upload progress bar (chunked-style UX)
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BACKEND_URL}/api/admin/document-templates`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.upload.onprogress = (evt) => {
          if (evt.lengthComputable) {
            setUploadPct(Math.round((evt.loaded / evt.total) * 100));
          }
        };
        xhr.onload = () => {
          let parsed = {};
          try { parsed = JSON.parse(xhr.responseText || '{}'); } catch (e) { /* noop */ }
          if (xhr.status >= 200 && xhr.status < 300) resolve(parsed);
          else reject(new Error(parsed?.detail || `HTTP ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(fd);
      });
      toast.success(`Uploaded "${data.name}"`);
      setShowUploader(false);
      setName(''); setDescription(''); setCategory('general'); setRequired(true); setFile(null);
      setUploadPct(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await load();
    } catch (e) { toast.error(`Upload failed: ${e.message}`); }
    finally { setBusy(false); }
  };

  const previewTemplate = async (t) => {
    const token = localStorage.getItem(TOKEN_KEY);
    // Iter132 — try document-templates/file first (DocuSign-style
    // library) then fall back to onboarding-templates/view (built-in
    // policy library) so Preview never 404s for Operational Order
    // Policy / Case Acceptance Policy etc.
    const urls = [
      `${BACKEND_URL}/api/admin/document-templates/${t.id}/file`,
      `${BACKEND_URL}/api/admin/onboarding-templates/${t.id}/view`,
    ];
    let lastErr = null;
    for (const url of urls) {
      try {
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const blob = await res.blob();
          setPreviewUrl(URL.createObjectURL(blob));
          setPreviewTitle(t.name);
          return;
        }
        lastErr = `HTTP ${res.status}`;
      } catch (e) {
        lastErr = e.message;
      }
    }
    toast.error(`Preview failed: ${lastErr || 'unknown'}`);
  };

  const remove = async (t) => {
    if (!window.confirm(`Archive template "${t.name}"? Documents already sent will still be accessible.`)) return;
    setBusy(true);
    try {
      await api(`/api/admin/document-templates/${t.id}`, { method: 'DELETE' });
      toast.success('Template archived');
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const total = templates.length;
  const totalSize = templates.reduce((s, t) => s + (t.file_size || 0), 0);

  return (
    <div className="nx-page space-y-4" data-testid="onboarding-documents-page">
      <NxPanel
        title="Onboarding Documents"
        subtitle="DOCUSIGN-STYLE PDF TEMPLATE LIBRARY · UPLOAD · PREVIEW · SEND"
        action={
          <div className="flex items-center gap-2 flex-wrap">
            <NxButton variant="ghost" className="nx-btn--sm" onClick={load} disabled={loading} data-testid="od-refresh">
              <RefreshCw size={14} /> Refresh
            </NxButton>
            <NxButton variant="primary" onClick={() => setShowUploader(true)} data-testid="od-upload-pdf">
              <UploadCloud size={14} /> Upload PDF
            </NxButton>
          </div>
        }
      >
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="od-summary">
          <div className="rounded-lg border-2 border-amber-400/30 bg-amber-500/5 p-3">
            <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-amber-300">PDF Templates</p>
            <p className="text-xl font-bold text-white mt-0.5">{total}</p>
          </div>
          <div className="rounded-lg border-2 border-cyan-400/30 bg-cyan-500/5 p-3">
            <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-cyan-300">Total Storage</p>
            <p className="text-xl font-bold text-white mt-0.5">{fmtSize(totalSize)}</p>
          </div>
          <div className="rounded-lg border-2 border-emerald-400/30 bg-emerald-500/5 p-3 col-span-2 md:col-span-1">
            <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-300">Workflow</p>
            <p className="text-xs text-slate-300 mt-0.5">Upload here → Send from Recruitment Center → Applicant signs in portal</p>
          </div>
        </div>
      </NxPanel>

      {/* Library */}
      {loading ? (
        <p className="text-slate-400 italic flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading templates…</p>
      ) : templates.length === 0 ? (
        <div className="rounded-lg border border-slate-700 bg-[#0E1626] p-6 text-center" data-testid="od-empty">
          <FileText className="w-10 h-10 text-slate-500 mx-auto mb-2" />
          <p className="text-sm text-slate-300">No document templates yet.</p>
          <p className="text-xs text-slate-500 mt-1 mb-3">Upload a PDF (W-9, I-9, NDA, etc.) and you&apos;ll be able to send it to applicants from the Recruitment Center.</p>
          <NxButton variant="primary" onClick={() => setShowUploader(true)} data-testid="od-upload-pdf-empty">
            <UploadCloud size={14} /> Upload your first PDF
          </NxButton>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="od-grid">
          {templates.map(t => {
            const tone = CATEGORY_TONE[t.category] || 'amber';
            return (
              <div key={t.id} className="rounded-lg border border-slate-700 bg-[#131A2A] p-4 flex flex-col gap-2" data-testid={`od-card-${t.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="w-9 h-9 rounded-md bg-[#0E1626] border border-slate-700 flex items-center justify-center flex-shrink-0">
                    <FileSignature className="w-5 h-5 text-amber-300" />
                  </div>
                  <div className="flex flex-wrap items-center gap-1 justify-end">
                    {t.required && <NxChip tone="red">Required</NxChip>}
                    <NxChip tone={tone}>{(t.category || 'general').toUpperCase()}</NxChip>
                  </div>
                </div>
                <h4 className="text-sm font-bold text-white break-words mt-1">{t.name}</h4>
                {t.description && <p className="text-xs text-slate-400 break-words">{t.description}</p>}
                <p className="text-[10px] font-mono text-slate-500">{fmtSize(t.file_size)} · uploaded {t.created_at ? new Date(t.created_at).toLocaleDateString() : '—'}</p>
                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-700/60 flex-wrap">
                  <NxButton variant="ghost" className="nx-btn--sm" onClick={() => previewTemplate(t)} data-testid={`od-preview-${t.id}`}>
                    <Eye size={14} /> Preview
                  </NxButton>
                  <NxButton variant="primary" className="nx-btn--sm" onClick={() => setEditingFieldsFor(t.id)} data-testid={`od-edit-fields-${t.id}`}>
                    <Edit3 size={14} /> Place Fields {Array.isArray(t.fields) && t.fields.length > 0 ? `(${t.fields.length})` : ''}
                  </NxButton>
                  <NxButton variant="danger" className="nx-btn--sm" onClick={() => remove(t)} disabled={busy} data-testid={`od-delete-${t.id}`}>
                    <Trash2 size={14} /> Archive
                  </NxButton>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload Modal */}
      {showUploader && (
        <div
          className="fixed inset-0 z-[60] bg-slate-950/85 backdrop-blur-sm flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget) setShowUploader(false); }}
          data-testid="od-uploader-overlay"
        >
          <div className="w-full max-w-xl bg-[#0B1220] border border-slate-700 rounded-lg shadow-2xl max-h-[96vh] overflow-hidden flex flex-col">
            <div className="border-b border-slate-700 px-4 py-3 flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-amber-300">Upload Document Template</p>
                <h2 className="text-white text-base font-bold">Add a PDF to the library</h2>
              </div>
              <button onClick={() => setShowUploader(false)} className="text-slate-400 hover:text-white" data-testid="od-uploader-close">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto">
              <div>
                <label className="text-[10px] font-mono uppercase tracking-[0.16em] text-slate-400">PDF File *</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={onPickFile}
                  className="block w-full text-sm text-slate-300 mt-1 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-amber-500 file:text-slate-900 file:font-semibold file:cursor-pointer"
                  data-testid="od-file-input"
                />
                {file && (
                  <p className="text-[11px] text-emerald-300 mt-1 font-mono">
                    <FileCheck2 size={12} className="inline mr-1" /> {file.name} · {fmtSize(file.size)}
                  </p>
                )}
                <p className="text-[10px] text-slate-500 mt-1 flex items-center gap-1">
                  <AlertCircle size={11} /> PDF only · 25MB max
                </p>
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase tracking-[0.16em] text-slate-400">Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g. NDA · Form W-9 · Direct Deposit Authorization"
                  className="w-full bg-[#0E1626] border border-slate-700 text-sm text-white rounded px-2 py-1.5 mt-0.5"
                  data-testid="od-name-input"
                />
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase tracking-[0.16em] text-slate-400">Description (optional)</label>
                <textarea
                  rows={2}
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Short description shown to the applicant."
                  className="w-full bg-[#0E1626] border border-slate-700 text-sm text-white rounded px-2 py-1.5 mt-0.5"
                  data-testid="od-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-mono uppercase tracking-[0.16em] text-slate-400">Category</label>
                  <select
                    value={category}
                    onChange={e => setCategory(e.target.value)}
                    className="w-full bg-[#0E1626] border border-slate-700 text-sm text-white rounded px-2 py-1.5 mt-0.5"
                    data-testid="od-category-select"
                  >
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <label className="flex items-end gap-2 text-sm text-slate-200 bg-[#0E1626] border border-slate-700 rounded px-2.5 py-1.5 cursor-pointer h-[34px] mt-[18px]">
                  <input
                    type="checkbox"
                    checked={required}
                    onChange={e => setRequired(e.target.checked)}
                    data-testid="od-required-checkbox"
                  />
                  Mark as required
                </label>
              </div>
            </div>
            <div className="border-t border-slate-700 px-4 py-3">
              {busy && (
                <div className="mb-3" data-testid="od-upload-progress">
                  <div className="flex items-center justify-between text-[11px] font-mono mb-1">
                    <span className="text-slate-300">Uploading…</span>
                    <span className="text-amber-300 font-bold">{uploadPct}%</span>
                  </div>
                  <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-500 transition-all" style={{ width: `${uploadPct}%` }} />
                  </div>
                </div>
              )}
              <div className="flex items-center justify-end gap-2">
                <NxButton variant="ghost" onClick={() => setShowUploader(false)} disabled={busy}>Cancel</NxButton>
                <NxButton variant="primary" onClick={upload} disabled={busy || !file || !name.trim()} data-testid="od-upload-submit">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud size={14} />} {busy ? `Uploading ${uploadPct}%` : 'Upload Template'}
                </NxButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewUrl && (
        <div
          className="fixed inset-0 z-[70] bg-slate-950/90 backdrop-blur-sm flex items-center justify-center p-1 sm:p-2"
          onClick={(e) => { if (e.target === e.currentTarget) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); } }}
          data-testid="od-preview-overlay"
        >
          <div className="bg-[#0B1220] border border-slate-700 rounded-lg w-full h-full sm:w-[95vw] sm:h-[95vh] max-w-none flex flex-col">
            <div className="border-b border-slate-700 px-4 py-2.5 flex items-center justify-between flex-shrink-0">
              <p className="text-sm text-white font-semibold truncate pr-2">{previewTitle}</p>
              <button onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }} className="text-slate-400 hover:text-white" data-testid="od-preview-close">
                <X size={18} />
              </button>
            </div>
            <iframe src={previewUrl} title="Template preview" className="flex-1 w-full bg-white" data-testid="od-preview-iframe" />
          </div>
        </div>
      )}

      {/* Field Editor */}
      {editingFieldsFor && (
        <TemplateFieldEditor
          templateId={editingFieldsFor}
          onClose={() => setEditingFieldsFor(null)}
          onSaved={load}
        />
      )}
    </div>
  );
};

export default OnboardingDocuments;
